import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { AccountRejectionReasons, RejectedAccount } from '@interfaces/models';
import { Store } from '@ngrx/store';
import { IneligibleAccountsContent } from '@shared/services/content/content-jsons/ineligible-account-content';
import * as fromRootStore from '@store/index';
import { selectClientFlagDigitalAdvise } from '@store/selectors/personal-data/personal-data.selectors';
import { LinkModule } from '@vg-constellation/angular-16/link';
import { ModalDialogModule } from '@vg-constellation/angular-16/modal-dialog';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-ineligible-accounts',
  templateUrl: './ineligible-accounts.component.html',
  styleUrls: ['./ineligible-accounts.component.scss'],
  standalone: true,
  imports: [LinkModule, ModalDialogModule, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IneligibleAccountsComponent {
  content = IneligibleAccountsContent;
  rejectionReasons = Object.values(AccountRejectionReasons);
  accountsByRejectionReason: Map<AccountRejectionReasons, RejectedAccount[]> = new Map();

  @Input() rejectedAccounts: RejectedAccount[] = [];
  @Input() isTransferDetailsFrom: boolean = false;

  isDigitalAdvice$: Observable<boolean>;
  isCashPlusEnabled$: Observable<boolean>;

  constructor(globalStore: Store<fromRootStore.State>) {
    this.isDigitalAdvice$ = globalStore.select(selectClientFlagDigitalAdvise);
    this.isCashPlusEnabled$ = globalStore.select(
      fromRootStore.selectCashPlusInternalTransferToggle,
    );
  }

  getModalContent() {
    return this.isTransferDetailsFrom ? this.content.modalBodyFrom : this.content.modalBodyTo;
  }

  getIneligibleAccountsByReason(reason: AccountRejectionReasons): RejectedAccount[] {
    //bandaid fix for duplicate accounts in rejected accounts array
    const set = new Set<string>();
    return this.rejectedAccounts.filter((account) => {
      const accountNum = account.accountNumber;
      if (set.has(accountNum)) {
        return false;
      }
      set.add(accountNum);
      return account.rejectionReason === reason;
    });
  }

  getRejectionReasonText(reason: AccountRejectionReasons, isDigitalAdvice: boolean): string | null {
    switch (reason) {
      case AccountRejectionReasons.NOT_PRIMARY_OWNER:
        return this.content.reasons.primaryOwnerOnly;
      case AccountRejectionReasons.MARGIN_OPTIONS_ACCOUNT:
        return this.content.reasons.marginOrOptions;
      case AccountRejectionReasons.NON_BROKERAGE_ACCOUNT:
        return this.content.reasons.nonBrokerageAccount;
      case AccountRejectionReasons.ZERO_BALANCE:
        return this.content.reasons.noBalance;
      case AccountRejectionReasons.MANAGED_ACCOUNT:
        return isDigitalAdvice
          ? this.content.reasons.digitalAdvice
          : this.content.reasons.personalAdvice;
      case AccountRejectionReasons.CASH_PLUS_ACCOUNT:
        return this.content.reasons.cashPlus;
      case AccountRejectionReasons.NON_MONEY_MARKET_POSITION_SELECTED:
        return this.content.reasons.nonMoneyMarketPositionSelected;
      case AccountRejectionReasons.ACCOUNT_TYPE:
        return this.content.reasons.accountType;
      case AccountRejectionReasons.ACTR_RESTRICTION:
        return this.content.reasons.actrRestriction;
      default:
        return null;
    }
  }
}
